export interface TvBenchmarkFrameMetrics {
  durationMs: number;
  frames: number;
  fps: number;
  medianFrameMs: number;
  p95FrameMs: number;
  p99FrameMs: number;
  maxFrameMs?: number;
  framesOver33Ms?: number;
  framesOver100Ms?: number;
  longFrames: number;
}

export interface TvBenchmarkRenderWorkMetrics {
  calls: number;
  durationMs: number;
  medianMs: number;
  p95Ms: number;
  p99Ms: number;
  maxMs: number;
  overBudgetCalls: number;
}

export interface TvBenchmarkSceneMetrics {
  frames: TvBenchmarkFrameMetrics;
  renderWork: TvBenchmarkRenderWorkMetrics;
}

export interface TvBenchmarkSoakWindow {
  minute: number;
  elapsedMs: number;
  metrics: TvBenchmarkSceneMetrics;
}

export interface TvBenchmarkSoakMetrics {
  kind: "fuse-runners-compatibility" | "webgl2-representative" | "webgl2-representative-stress";
  requestedDurationMs: number;
  completedDurationMs: number;
  targetFps?: number;
  stressLabel?: string;
  aggregate: TvBenchmarkSceneMetrics;
  windows: TvBenchmarkSoakWindow[];
}

export interface TvBenchmarkWebGlStressLevel {
  label: string;
  instances: number;
  passes: number;
  workUnits: number;
  metrics: TvBenchmarkFrameMetrics;
}

export function webGlLongevityProfile(): {
  label: string;
  instances: number;
  passes: number;
  workUnits: number;
  targetFps: number;
} {
  return {
    label: "representative battle",
    instances: 448,
    passes: 3,
    workUnits: 448 * 3,
    targetFps: 30,
  };
}

export function selectWebGlSoakStressLevel(
  levels: TvBenchmarkWebGlStressLevel[],
): TvBenchmarkWebGlStressLevel | null {
  let selected: TvBenchmarkWebGlStressLevel | null = null;
  for (const level of levels) {
    if (!isWebGlStressLevelSafeForSoak(level)) break;
    selected = level;
  }
  return selected;
}

export function isWebGlStressLevelSafeForSoak(level: TvBenchmarkWebGlStressLevel): boolean {
  const maximumFrameMs = level.metrics.maxFrameMs ?? level.metrics.p99FrameMs;
  return (
    level.metrics.fps >= 30 &&
    level.metrics.p95FrameMs <= 50 &&
    maximumFrameMs < 100 &&
    (level.metrics.framesOver100Ms ?? 0) === 0
  );
}

export interface TvBenchmarkReport {
  benchmarkVersion: number;
  capturedAt: string;
  environment: {
    userAgent: string;
    browserName: string;
    browserVersion: string;
    platform: string;
    language: string;
    reportedTvModel: string | null;
    detectedTvModel: string | null;
    viewport: string;
    screen: string;
    devicePixelRatio: number;
    hardwareConcurrency: number | null;
    deviceMemoryGb: number | null;
  };
  features: {
    canvas2d: boolean;
    webgl1: boolean;
    webgl2: boolean;
    webAudio: boolean;
    gamepadApi: boolean;
    localStorage: boolean;
    webWorkers: boolean;
  };
  display: TvBenchmarkFrameMetrics;
  canvas2d720p: TvBenchmarkFrameMetrics;
  representativeScenes?: {
    fuseRunnersCompatibility: TvBenchmarkSceneMetrics;
    fuseRunnersRich: TvBenchmarkSceneMetrics;
  };
  javascript: {
    durationMs: number;
    updates: number;
    updatesPerSecond: number;
    checksum: number;
  };
  webgl2: {
    available: boolean;
    metrics: TvBenchmarkFrameMetrics | null;
    representativeMetrics?: TvBenchmarkFrameMetrics | null;
    stressLevels?: TvBenchmarkWebGlStressLevel[];
    soak?: TvBenchmarkSoakMetrics;
    vendor: string;
    renderer: string;
    version: string;
    shadingLanguageVersion: string;
    maxTextureSize: number;
    maxRenderbufferSize: number;
    maxTextureUnits: number;
    maxVertexTextureUnits: number;
    maxSamples: number;
    contextRecovery: "passed" | "failed" | "unsupported" | "not-run";
    error?: string;
  };
  soak?: TvBenchmarkSoakMetrics;
  classification: {
    tier:
      | "below-baseline"
      | "canvas-baseline"
      | "canvas-enhanced"
      | "webgl2-capable"
      | "webgl2-enhanced";
    title: string;
    summary: string;
    meetsMinimum: boolean;
    recommendedFusePreset?: "compatibility" | "canvas-rich";
    fuseRunnersCompatibilityPass?: boolean | null;
    fuseRunnersRichPass?: boolean | null;
  };
}

export interface TvBenchmarkProgressReport {
  kind: "webgl2-soak-progress";
  schemaVersion: 1;
  runId: string;
  benchmarkVersion: number;
  capturedAt: string;
  status: "started" | "running" | "completed" | "failed" | "context-lost" | "page-hidden";
  sequence: number;
  elapsedMs: number;
  targetDurationMs: 600_000;
  checkpointIntervalMs: number;
  pageVisibility: "visible" | "hidden";
  contextLost: boolean;
  profile: {
    label: string;
    instances: number;
    passes: number;
    workUnits: number;
    targetFps: number;
    width: number;
    height: number;
  };
  environment: TvBenchmarkReport["environment"];
  webgl2: Pick<
    TvBenchmarkReport["webgl2"],
    | "vendor"
    | "renderer"
    | "version"
    | "shadingLanguageVersion"
    | "maxTextureSize"
    | "maxRenderbufferSize"
    | "maxTextureUnits"
    | "maxVertexTextureUnits"
    | "maxSamples"
  >;
  aggregate?: TvBenchmarkSceneMetrics;
  latestWindow?: TvBenchmarkSceneMetrics;
  lastFrameGapMs?: number;
  error?: string;
}

export type TvBenchmarkSubmission = TvBenchmarkReport | TvBenchmarkProgressReport;

export function parseTvBenchmarkSubmission(value: unknown): TvBenchmarkSubmission | null {
  if (isRecord(value) && value.kind === "webgl2-soak-progress") {
    return isProgressReport(value) ? (value as unknown as TvBenchmarkProgressReport) : null;
  }
  return parseTvBenchmarkReport(value);
}

export function parseTvBenchmarkReport(value: unknown): TvBenchmarkReport | null {
  if (!isRecord(value)) return null;
  if (!isInteger(value.benchmarkVersion, 1, 100)) return null;
  if (!isTimestamp(value.capturedAt)) return null;
  if (!isEnvironment(value.environment)) return null;
  if (!isFeatures(value.features)) return null;
  if (!isFrameMetrics(value.display) || !isFrameMetrics(value.canvas2d720p)) return null;
  if (
    value.representativeScenes !== undefined &&
    !isRepresentativeScenes(value.representativeScenes)
  )
    return null;
  if (!isJavascriptMetrics(value.javascript)) return null;
  if (!isWebGlDetails(value.webgl2)) return null;
  if (value.soak !== undefined && !isSoakMetrics(value.soak)) return null;
  if (!isClassification(value.classification)) return null;
  return value as unknown as TvBenchmarkReport;
}

function isProgressReport(value: Record<string, unknown>): boolean {
  if (
    value.kind !== "webgl2-soak-progress" ||
    value.schemaVersion !== 1 ||
    !isString(value.runId, 8, 120) ||
    !/^[A-Za-z0-9._-]+$/.test(value.runId) ||
    !isInteger(value.benchmarkVersion, 1, 100) ||
    !isTimestamp(value.capturedAt) ||
    !["started", "running", "completed", "failed", "context-lost", "page-hidden"].includes(
      value.status as string,
    ) ||
    !isInteger(value.sequence, 0, 1_000) ||
    !isFiniteNumber(value.elapsedMs, 0, 620_000) ||
    value.targetDurationMs !== 600_000 ||
    !isInteger(value.checkpointIntervalMs, 5_000, 60_000) ||
    (value.pageVisibility !== "visible" && value.pageVisibility !== "hidden") ||
    typeof value.contextLost !== "boolean" ||
    !isEnvironment(value.environment) ||
    !isProgressProfile(value.profile) ||
    !isProgressWebGlDetails(value.webgl2)
  )
    return false;
  if (value.aggregate !== undefined && !isSceneMetrics(value.aggregate, 620_000)) return false;
  if (value.latestWindow !== undefined && !isSceneMetrics(value.latestWindow, 60_000)) return false;
  if (value.lastFrameGapMs !== undefined && !isFiniteNumber(value.lastFrameGapMs, 0, 620_000))
    return false;
  return value.error === undefined || isString(value.error, 0, 500);
}

function isProgressProfile(value: unknown): boolean {
  return (
    isRecord(value) &&
    isString(value.label, 1, 120) &&
    isInteger(value.instances, 1, 2_000_000) &&
    isInteger(value.passes, 1, 128) &&
    isInteger(value.workUnits, 1, 100_000_000) &&
    isFiniteNumber(value.targetFps, 1, 240) &&
    isInteger(value.width, 1, 8_192) &&
    isInteger(value.height, 1, 8_192)
  );
}

function isProgressWebGlDetails(value: unknown): boolean {
  if (
    !isRecord(value) ||
    !isString(value.vendor, 0, 300) ||
    !isString(value.renderer, 0, 300) ||
    !isString(value.version, 0, 200) ||
    !isString(value.shadingLanguageVersion, 0, 200)
  )
    return false;
  return [
    "maxTextureSize",
    "maxRenderbufferSize",
    "maxTextureUnits",
    "maxVertexTextureUnits",
    "maxSamples",
  ].every((key) => isInteger(value[key], 0, 1_000_000));
}

function isEnvironment(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    isString(value.userAgent, 1, 600) &&
    isString(value.browserName, 1, 60) &&
    isString(value.browserVersion, 1, 80) &&
    isString(value.platform, 0, 120) &&
    isString(value.language, 0, 40) &&
    isNullableString(value.reportedTvModel, 120) &&
    isNullableString(value.detectedTvModel, 120) &&
    isDisplaySize(value.viewport) &&
    isDisplaySize(value.screen) &&
    isFiniteNumber(value.devicePixelRatio, 0.1, 16) &&
    isNullableNumber(value.hardwareConcurrency, 1, 1_024) &&
    isNullableNumber(value.deviceMemoryGb, 0.1, 1_024)
  );
}

function isFeatures(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return [
    "canvas2d",
    "webgl1",
    "webgl2",
    "webAudio",
    "gamepadApi",
    "localStorage",
    "webWorkers",
  ].every((key) => typeof value[key] === "boolean");
}

function isFrameMetrics(value: unknown, maximumDurationMs = 60_000): boolean {
  if (!isRecord(value)) return false;
  return (
    isFiniteNumber(value.durationMs, 0, maximumDurationMs) &&
    isInteger(value.frames, 0, 100_000) &&
    isFiniteNumber(value.fps, 0, 1_000) &&
    isFiniteNumber(value.medianFrameMs, 0, 60_000) &&
    isFiniteNumber(value.p95FrameMs, 0, 60_000) &&
    isFiniteNumber(value.p99FrameMs, 0, 60_000) &&
    (value.maxFrameMs === undefined || isFiniteNumber(value.maxFrameMs, 0, 60_000)) &&
    (value.framesOver33Ms === undefined || isInteger(value.framesOver33Ms, 0, 100_000)) &&
    (value.framesOver100Ms === undefined || isInteger(value.framesOver100Ms, 0, 100_000)) &&
    isInteger(value.longFrames, 0, 100_000)
  );
}

function isJavascriptMetrics(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    isFiniteNumber(value.durationMs, 0, 60_000) &&
    isInteger(value.updates, 0, 1_000_000_000) &&
    isFiniteNumber(value.updatesPerSecond, 0, 1_000_000_000) &&
    isFiniteNumber(value.checksum, -1_000_000_000, 1_000_000_000)
  );
}

function isRepresentativeScenes(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return isSceneMetrics(value.fuseRunnersCompatibility) && isSceneMetrics(value.fuseRunnersRich);
}

function isSceneMetrics(value: unknown, maximumDurationMs = 60_000): boolean {
  return (
    isRecord(value) &&
    isFrameMetrics(value.frames, maximumDurationMs) &&
    isRecord(value.renderWork) &&
    isInteger(value.renderWork.calls, 0, 100_000) &&
    isFiniteNumber(value.renderWork.durationMs, 0, maximumDurationMs) &&
    isFiniteNumber(value.renderWork.medianMs, 0, 60_000) &&
    isFiniteNumber(value.renderWork.p95Ms, 0, 60_000) &&
    isFiniteNumber(value.renderWork.p99Ms, 0, 60_000) &&
    isFiniteNumber(value.renderWork.maxMs, 0, 60_000) &&
    isInteger(value.renderWork.overBudgetCalls, 0, 100_000)
  );
}

function isSoakMetrics(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (
    (value.kind !== "fuse-runners-compatibility" &&
      value.kind !== "webgl2-representative" &&
      value.kind !== "webgl2-representative-stress") ||
    value.requestedDurationMs !== 600_000 ||
    !isFiniteNumber(value.completedDurationMs, 600_000, 620_000) ||
    (value.targetFps !== undefined && !isFiniteNumber(value.targetFps, 1, 240)) ||
    (value.stressLabel !== undefined && !isString(value.stressLabel, 1, 120)) ||
    !isSceneMetrics(value.aggregate, 620_000) ||
    !Array.isArray(value.windows) ||
    value.windows.length < 1 ||
    value.windows.length > 10
  )
    return false;
  return value.windows.every(
    (window, index) =>
      isRecord(window) &&
      window.minute === index + 1 &&
      isFiniteNumber(window.elapsedMs, (index + 1) * 60_000, 620_000) &&
      isSceneMetrics(window.metrics, 620_000),
  );
}

function isWebGlDetails(value: unknown): boolean {
  if (!isRecord(value) || typeof value.available !== "boolean") return false;
  if (value.metrics !== null && !isFrameMetrics(value.metrics)) return false;
  if (
    value.representativeMetrics !== undefined &&
    value.representativeMetrics !== null &&
    !isFrameMetrics(value.representativeMetrics)
  )
    return false;
  if (
    value.stressLevels !== undefined &&
    (!Array.isArray(value.stressLevels) ||
      value.stressLevels.length < 1 ||
      value.stressLevels.length > 12 ||
      !value.stressLevels.every(
        (level) =>
          isRecord(level) &&
          isString(level.label, 1, 120) &&
          isInteger(level.instances, 1, 2_000_000) &&
          isInteger(level.passes, 1, 128) &&
          isInteger(level.workUnits, 1, 100_000_000) &&
          isFrameMetrics(level.metrics),
      ))
  )
    return false;
  if (value.soak !== undefined && !isSoakMetrics(value.soak)) return false;
  if (
    !isString(value.vendor, 0, 300) ||
    !isString(value.renderer, 0, 300) ||
    !isString(value.version, 0, 200) ||
    !isString(value.shadingLanguageVersion, 0, 200)
  )
    return false;
  for (const key of [
    "maxTextureSize",
    "maxRenderbufferSize",
    "maxTextureUnits",
    "maxVertexTextureUnits",
    "maxSamples",
  ]) {
    if (!isInteger(value[key], 0, 1_000_000)) return false;
  }
  if (!["passed", "failed", "unsupported", "not-run"].includes(value.contextRecovery as string))
    return false;
  return value.error === undefined || isString(value.error, 0, 500);
}

function isClassification(value: unknown): boolean {
  if (!isRecord(value)) return false;
  return (
    [
      "below-baseline",
      "canvas-baseline",
      "canvas-enhanced",
      "webgl2-capable",
      "webgl2-enhanced",
    ].includes(value.tier as string) &&
    isString(value.title, 1, 160) &&
    isString(value.summary, 1, 1_000) &&
    typeof value.meetsMinimum === "boolean" &&
    (value.recommendedFusePreset === undefined ||
      value.recommendedFusePreset === "compatibility" ||
      value.recommendedFusePreset === "canvas-rich") &&
    (value.fuseRunnersCompatibilityPass === undefined ||
      value.fuseRunnersCompatibilityPass === null ||
      typeof value.fuseRunnersCompatibilityPass === "boolean") &&
    (value.fuseRunnersRichPass === undefined ||
      value.fuseRunnersRichPass === null ||
      typeof value.fuseRunnersRichPass === "boolean")
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isString(value: unknown, min: number, max: number): value is string {
  return typeof value === "string" && value.length >= min && value.length <= max;
}

function isNullableString(value: unknown, max: number): boolean {
  return value === null || isString(value, 1, max);
}

function isFiniteNumber(value: unknown, min: number, max: number): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
}

function isNullableNumber(value: unknown, min: number, max: number): boolean {
  return value === null || isFiniteNumber(value, min, max);
}

function isInteger(value: unknown, min: number, max: number): value is number {
  return Number.isInteger(value) && isFiniteNumber(value, min, max);
}

function isTimestamp(value: unknown): value is string {
  return isString(value, 1, 80) && !Number.isNaN(Date.parse(value));
}

function isDisplaySize(value: unknown): value is string {
  return typeof value === "string" && /^\d{1,6}×\d{1,6}$/.test(value);
}
